import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../components/ui/Toast";
import {
  Button,
  Input,
  Select,
  Card,
  DataTable,
  Modal,
  Badge,
  Textarea,
} from "../../components/ui";

const TIPO_UTENZA_OPTIONS = [
  { value: "", label: "Tutti i tipi" },
  { value: "100000000", label: "Energia elettrica" },
  { value: "100000001", label: "Gas naturale" },
  { value: "100000002", label: "Acqua" },
  { value: "100000003", label: "Teleriscaldamento" },
  { value: "100000004", label: "Telecomunicazioni" },
  { value: "100000005", label: "Rifiuti" },
  { value: "100000006", label: "Altro" },
];

const DEFAULT_TEMPLATE_CONFIG = {
  version: "1.0",
  patterns: {
    pod: { regex: "POD[:\\\\s]*([A-Z0-9]{14,16})", group: 1 },
    importoTotale: {
      regex: "Totale[:\\\\s]*€?\\\\s*([\\\\d.,]+)",
      group: 1,
      transform: "parseDecimal",
    },
    periodoInizio: {
      regex: "dal\\\\s*(\\\\d{2}/\\\\d{2}/\\\\d{4})",
      group: 1,
      dateFormat: "dd/MM/yyyy",
    },
    periodoFine: {
      regex: "al\\\\s*(\\\\d{2}/\\\\d{2}/\\\\d{4})",
      group: 1,
      dateFormat: "dd/MM/yyyy",
    },
    consumo: {
      regex: "Consumo[\\\\s:]*(\\\\d+[.,]?\\\\d*)\\\\s*(kWh|Smc|mc)",
      groups: [1, 2],
    },
    numeroFattura: {
      regex: "Fattura\\\\s*n[.°]?\\\\s*([A-Z0-9/\\\\-]+)",
      group: 1,
    },
  },
  fallbackKeywords: ["energia", "elettrica", "gas", "POD", "PDR"],
};

function PdfTemplatesPage() {
  const { apiRequest } = useAuth();
  const { showToast } = useToast();

  const [templates, setTemplates] = useState([]);
  const [fornitori, setFornitori] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    nome: "",
    fornitoreId: "",
    tipoutenza: "",
    configurazioneTemplate: JSON.stringify(DEFAULT_TEMPLATE_CONFIG, null, 2),
    attivo: true,
    priorita: 10,
    note: "",
  });

  // Test state
  const [testFile, setTestFile] = useState(null);
  const [testTemplateId, setTestTemplateId] = useState("");

  // Load data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [templateData, fornitoriData] = await Promise.all([
          apiRequest("/pdf-templates"),
          apiRequest("/fornitori").catch(() => ({ value: [] })),
        ]);
        setTemplates(templateData || []);
        setFornitori(fornitoriData.value || []);
      } catch (error) {
        console.error("Error loading data:", error);
        showToast("Errore nel caricamento", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [apiRequest, showToast]);

  const openCreate = () => {
    setEditingItem(null);
    setFormData({
      nome: "",
      fornitoreId: "",
      tipoutenza: "",
      configurazioneTemplate: JSON.stringify(DEFAULT_TEMPLATE_CONFIG, null, 2),
      attivo: true,
      priorita: 10,
      note: "",
    });
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setFormData({
      nome: item.nome || "",
      fornitoreId: item.fornitoreId || "",
      tipoutenza: item.tipoutenza !== null ? String(item.tipoutenza) : "",
      configurazioneTemplate: item.configurazioneTemplate
        ? JSON.stringify(item.configurazioneTemplate, null, 2)
        : JSON.stringify(DEFAULT_TEMPLATE_CONFIG, null, 2),
      attivo: item.attivo !== false,
      priorita: item.priorita || 10,
      note: item.note || "",
    });
    setModalOpen(true);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSave = async () => {
    if (!formData.nome || !formData.fornitoreId) {
      showToast("Nome e fornitore sono obbligatori", "warning");
      return;
    }

    // Validate JSON
    try {
      JSON.parse(formData.configurazioneTemplate);
    } catch {
      showToast("La configurazione template non è un JSON valido", "warning");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        nome: formData.nome,
        fornitoreId: formData.fornitoreId,
        tipoutenza: formData.tipoutenza || null,
        configurazioneTemplate: formData.configurazioneTemplate,
        attivo: formData.attivo,
        priorita: parseInt(formData.priorita) || 10,
        note: formData.note || null,
      };

      if (editingItem) {
        await apiRequest(`/pdf-templates/${editingItem.id}`, {
          method: "PUT",
          body: JSON.stringify(payload),
        });
        showToast("Template aggiornato", "success");
      } else {
        await apiRequest("/pdf-templates", {
          method: "POST",
          body: JSON.stringify(payload),
        });
        showToast("Template creato", "success");
      }

      setModalOpen(false);
      // Reload
      const data = await apiRequest("/pdf-templates");
      setTemplates(data || []);
    } catch (error) {
      console.error("Error saving template:", error);
      showToast("Errore nel salvataggio", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    setSaving(true);
    try {
      await apiRequest(`/pdf-templates/${itemToDelete.id}`, {
        method: "DELETE",
      });
      showToast("Template eliminato", "success");
      setDeleteModalOpen(false);
      setItemToDelete(null);
      setTemplates((prev) => prev.filter((t) => t.id !== itemToDelete.id));
    } catch (error) {
      console.error("Error deleting template:", error);
      showToast("Errore nell'eliminazione", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testFile) {
      showToast("Seleziona un file PDF", "warning");
      return;
    }

    setTesting(true);
    setTestResult(null);
    try {
      const fd = new FormData();
      fd.append("file", testFile);
      if (testTemplateId) {
        fd.append("templateId", testTemplateId);
      }

      const result = await apiRequest("/pdf-templates/test", {
        method: "POST",
        body: fd,
        headers: {}, // Let browser set Content-Type for FormData
      });

      setTestResult(result);
      showToast("Test completato", "success");
    } catch (error) {
      console.error("Error testing:", error);
      showToast("Errore nel test: " + (error.message || ""), "error");
    } finally {
      setTesting(false);
    }
  };

  const columns = [
    {
      key: "nome",
      label: "Nome Template",
      render: (row) => <span className="font-medium">{row.nome}</span>,
    },
    {
      key: "fornitore",
      label: "Fornitore",
      render: (row) => row.fornitoreNome || "-",
    },
    {
      key: "tipo",
      label: "Tipo Utenza",
      render: (row) => row.tipoutenzaLabel || "Tutti",
    },
    {
      key: "priorita",
      label: "Priorità",
      render: (row) => row.priorita || "-",
    },
    {
      key: "stato",
      label: "Stato",
      render: (row) =>
        row.attivo ? (
          <Badge variant="success">Attivo</Badge>
        ) : (
          <Badge variant="default">Inattivo</Badge>
        ),
    },
    {
      key: "azioni",
      label: "",
      render: (row) => (
        <div className="flex gap-1">
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              openEdit(row);
            }}
          >
            Modifica
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setItemToDelete(row);
              setDeleteModalOpen(true);
            }}
          >
            Elimina
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Template PDF Bollette
          </h1>
          <p className="text-gray-500 mt-1">
            Configurazione pattern di estrazione dati da bollette PDF
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => {
              setTestFile(null);
              setTestTemplateId("");
              setTestResult(null);
              setTestModalOpen(true);
            }}
          >
            Test PDF
          </Button>
          <Button onClick={openCreate}>+ Nuovo Template</Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4">
          <p className="text-sm text-gray-500">Totale template</p>
          <p className="text-2xl font-bold">{templates.length}</p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-gray-500">Attivi</p>
          <p className="text-2xl font-bold text-green-600">
            {templates.filter((t) => t.attivo).length}
          </p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-gray-500">Fornitori configurati</p>
          <p className="text-2xl font-bold">
            {new Set(templates.map((t) => t.fornitoreId).filter(Boolean)).size}
          </p>
        </Card>
      </div>

      {/* Table */}
      <Card>
        <DataTable
          columns={columns}
          data={templates}
          loading={loading}
          emptyMessage="Nessun template configurato"
        />
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingItem ? "Modifica Template" : "Nuovo Template"}
        size="lg"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Nome Template *"
              name="nome"
              value={formData.nome}
              onChange={handleFormChange}
              placeholder="es. ENEL Servizio Elettrico"
            />
            <Select
              label="Fornitore *"
              name="fornitoreId"
              value={formData.fornitoreId}
              onChange={handleFormChange}
              options={[
                { value: "", label: "Seleziona fornitore" },
                ...fornitori.map((f) => ({
                  value: f.id,
                  label: f.nome,
                })),
              ]}
            />
            <Select
              label="Tipo Utenza"
              name="tipoutenza"
              value={formData.tipoutenza}
              onChange={handleFormChange}
              options={TIPO_UTENZA_OPTIONS}
            />
            <Input
              label="Priorità"
              name="priorita"
              type="number"
              value={formData.priorita}
              onChange={handleFormChange}
              placeholder="1 = alta"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Configurazione Template (JSON)
            </label>
            <textarea
              name="configurazioneTemplate"
              value={formData.configurazioneTemplate}
              onChange={handleFormChange}
              rows={12}
              className="w-full border rounded-md px-3 py-2 font-mono text-sm"
              placeholder="JSON con pattern regex per estrazione dati"
            />
            <p className="text-xs text-gray-500 mt-1">
              Definisci i pattern regex per estrarre: pod, importoTotale,
              periodoInizio, periodoFine, consumo, numeroFattura
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="attivo"
              name="attivo"
              checked={formData.attivo}
              onChange={handleFormChange}
              className="rounded border-gray-300"
            />
            <label
              htmlFor="attivo"
              className="text-sm font-medium text-gray-700"
            >
              Template attivo
            </label>
          </div>
          <Textarea
            label="Note"
            name="note"
            value={formData.note}
            onChange={handleFormChange}
            rows={2}
          />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              Annulla
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving
                ? "Salvataggio..."
                : editingItem
                  ? "Salva Modifiche"
                  : "Crea Template"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Test Modal */}
      <Modal
        isOpen={testModalOpen}
        onClose={() => setTestModalOpen(false)}
        title="Test Parsing PDF"
        size="lg"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              File PDF *
            </label>
            <input
              type="file"
              accept=".pdf"
              onChange={(e) => setTestFile(e.target.files[0])}
              className="w-full border rounded-md px-3 py-2"
            />
          </div>
          <Select
            label="Template (opzionale)"
            value={testTemplateId}
            onChange={(e) => setTestTemplateId(e.target.value)}
            options={[
              {
                value: "",
                label: "Nessuno (usa fallback patterns)",
              },
              ...templates.map((t) => ({
                value: t.id,
                label: `${t.nome} (${t.fornitoreNome || "?"})`,
              })),
            ]}
          />
          <Button onClick={handleTest} disabled={testing || !testFile}>
            {testing ? "Analisi in corso..." : "Testa PDF"}
          </Button>

          {/* Test results */}
          {testResult && (
            <div className="mt-4 space-y-3">
              <h3 className="font-semibold text-lg">Risultato</h3>
              <div className="bg-gray-50 rounded p-3 text-sm">
                <p>
                  <strong>File:</strong> {testResult.fileName} (
                  {Math.round(testResult.fileSize / 1024)} KB)
                </p>
                <p>
                  <strong>Template usato:</strong>{" "}
                  {testResult.templateUsed ? "Si" : "No (fallback)"}
                </p>
              </div>

              {testResult.result && (
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {testResult.result.pod && (
                      <div className="bg-green-50 p-2 rounded">
                        <strong>POD/PDR:</strong> {testResult.result.pod}
                      </div>
                    )}
                    {testResult.result.importoTotale !== null && (
                      <div className="bg-green-50 p-2 rounded">
                        <strong>Importo:</strong> €
                        {testResult.result.importoTotale}
                      </div>
                    )}
                    {testResult.result.periodoInizio && (
                      <div className="bg-blue-50 p-2 rounded">
                        <strong>Periodo:</strong>{" "}
                        {testResult.result.periodoInizio} -{" "}
                        {testResult.result.periodoFine}
                      </div>
                    )}
                    {testResult.result.consumoQuantita !== null && (
                      <div className="bg-blue-50 p-2 rounded">
                        <strong>Consumo:</strong>{" "}
                        {testResult.result.consumoQuantita}{" "}
                        {testResult.result.consumoUnita}
                      </div>
                    )}
                    {testResult.result.numeroFattura && (
                      <div className="bg-gray-100 p-2 rounded">
                        <strong>Fattura:</strong>{" "}
                        {testResult.result.numeroFattura}
                      </div>
                    )}
                    {testResult.result.confidence !== undefined && (
                      <div
                        className={`p-2 rounded ${
                          testResult.result.confidence >= 70
                            ? "bg-green-50"
                            : testResult.result.confidence >= 40
                              ? "bg-yellow-50"
                              : "bg-red-50"
                        }`}
                      >
                        <strong>Confidenza:</strong>{" "}
                        {testResult.result.confidence}%
                      </div>
                    )}
                  </div>

                  {testResult.result.warnings?.length > 0 && (
                    <div className="bg-yellow-50 p-2 rounded text-sm">
                      <strong>Warning:</strong>
                      <ul className="list-disc ml-4">
                        {testResult.result.warnings.map((w, i) => (
                          <li key={i}>{w}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {testResult.result.errors?.length > 0 && (
                    <div className="bg-red-50 p-2 rounded text-sm">
                      <strong>Errori:</strong>
                      <ul className="list-disc ml-4">
                        {testResult.result.errors.map((e, i) => (
                          <li key={i}>{e}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Raw extracted text preview */}
                  {testResult.result.rawText && (
                    <details className="text-sm">
                      <summary className="cursor-pointer font-medium text-gray-600">
                        Testo estratto dal PDF
                      </summary>
                      <pre className="mt-2 bg-gray-100 p-2 rounded text-xs max-h-48 overflow-auto whitespace-pre-wrap">
                        {testResult.result.rawText.substring(0, 2000)}
                        {testResult.result.rawText.length > 2000 && "..."}
                      </pre>
                    </details>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>

      {/* Delete Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Conferma Eliminazione"
      >
        <div className="space-y-4">
          <p>
            Sei sicuro di voler eliminare il template{" "}
            <strong>{itemToDelete?.nome}</strong>?
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
              Annulla
            </Button>
            <Button variant="danger" onClick={handleDelete} disabled={saving}>
              {saving ? "Eliminazione..." : "Elimina"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default PdfTemplatesPage;
